import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import MensajeCargando from '../UI/MensajeCargando.js';
import MensajeError from '../UI/MensajeError.js';
import CeldaOrdenable from '../UI/CeldaOrdenable.js';
import useGetURL from '../../hooks/useGetURL.js';
import * as FuncionesOrdenamiento from '../funciones/FuncionesOrdenamiento.js';
import styles from './Enfrentamientos.module.css';

const BaseEnfrentamientos = () => {
  const { idPais } = useParams();
  const [searchParams] = useSearchParams();
  const pais = searchParams.get('pais');

  const [rivales, setRivales] = useState([]);
  const [ordenPor, setOrdenPor] = useState(FuncionesOrdenamiento.CamposOrdenamiento.RIVAL);
  const [ascendente, setAscendente] = useState(true);
  const { isCargando, erroresGet, fetchData, resetErroresGet } = useGetURL();

  useEffect(() => {
    document.title = 'Enfrentamientos - Base Única';
  }, []);

  useEffect(() => {
    resetErroresGet();
  }, [resetErroresGet]);

  useEffect(() => {
    const paisId = idPais || pais;
    if (paisId) {
      // Llamar al endpoint: GET /selecciones_pais/{idPais}/rivales/totales?codCategoria=1&tipoInternacional=A
      fetchData(
        `selecciones_pais/${paisId}/rivales/totales?codCategoria=1&tipoInternacional=A`,
        setRivales
      );
    }
  }, [idPais, pais, fetchData]);

  // Función para calcular totales
  const calcularTotales = () => {
    if (!rivales || rivales.length === 0) {
      return {
        totalPartidos: 0,
        ganados: 0,
        empatados: 0,
        perdidos: 0,
        golesAFavor: 0,
        golesEnContra: 0,
      };
    }

    return {
      totalPartidos: rivales.reduce((sum, r) => sum + (r.totalPartidos || 0), 0),
      ganados: rivales.reduce((sum, r) => sum + (r.ganados || 0), 0),
      empatados: rivales.reduce((sum, r) => sum + (r.empatados || 0), 0),
      perdidos: rivales.reduce((sum, r) => sum + (r.perdidos || 0), 0),
      golesAFavor: rivales.reduce((sum, r) => sum + (r.golesAFavor || 0), 0),
      golesEnContra: rivales.reduce((sum, r) => sum + (r.golesEnContra || 0), 0),
    };
  };

  const totales = calcularTotales();

  if (isCargando) {
    return <MensajeCargando />;
  }

  return (
    <div className={styles.contenedor}>
      <h1>Enfrentamientos</h1>

      {erroresGet && erroresGet.length > 0 && <MensajeError errores={erroresGet} />}

      {rivales && rivales.length > 0 && (
        <div className={styles.tablaContenedor}>
          <table className={styles.tabla}>
            <thead>
              <tr>
                <CeldaOrdenable
                  campo={FuncionesOrdenamiento.CamposOrdenamiento.RIVAL}
                  label="Rival"
                  ordenPor={ordenPor}
                  ascendente={ascendente}
                  onChange={(campo, asc) => {
                    setOrdenPor(campo);
                    setAscendente(asc);
                  }}
                  className={styles.headerRival}
                  title="Click para ordenar por rival"
                />
                <CeldaOrdenable
                  campo={FuncionesOrdenamiento.CamposOrdenamiento.PARTIDOS}
                  label="PJ"
                  ordenPor={ordenPor}
                  ascendente={ascendente}
                  onChange={(campo, asc) => {
                    setOrdenPor(campo);
                    setAscendente(asc);
                  }}
                  className={styles.headerTotal}
                  title="Partidos jugados"
                />
                <CeldaOrdenable
                  campo={FuncionesOrdenamiento.CamposOrdenamiento.GANADOS}
                  label="PG"
                  ordenPor={ordenPor}
                  ascendente={ascendente}
                  onChange={(campo, asc) => {
                    setOrdenPor(campo);
                    setAscendente(asc);
                  }}
                  className={styles.headerPartidos}
                  title="Partidos ganados"
                />
                <CeldaOrdenable
                  campo={FuncionesOrdenamiento.CamposOrdenamiento.EMPATADOS}
                  label="PE"
                  ordenPor={ordenPor}
                  ascendente={ascendente}
                  onChange={(campo, asc) => {
                    setOrdenPor(campo);
                    setAscendente(asc);
                  }}
                  className={styles.headerPartidos}
                  title="Partidos empatados"
                />
                <CeldaOrdenable
                  campo={FuncionesOrdenamiento.CamposOrdenamiento.PERDIDOS}
                  label="PP"
                  ordenPor={ordenPor}
                  ascendente={ascendente}
                  onChange={(campo, asc) => {
                    setOrdenPor(campo);
                    setAscendente(asc);
                  }}
                  className={styles.headerPartidos}
                  title="Partidos perdidos"
                />
                <CeldaOrdenable
                  campo={FuncionesOrdenamiento.CamposOrdenamiento.GOLES_FAVOR}
                  label="GF"
                  ordenPor={ordenPor}
                  ascendente={ascendente}
                  onChange={(campo, asc) => {
                    setOrdenPor(campo);
                    setAscendente(asc);
                  }}
                  className={styles.headerGoles}
                  title="Goles a favor"
                />
                <CeldaOrdenable
                  campo={FuncionesOrdenamiento.CamposOrdenamiento.GOLES_CONTRA}
                  label="GC"
                  ordenPor={ordenPor}
                  ascendente={ascendente}
                  onChange={(campo, asc) => {
                    setOrdenPor(campo);
                    setAscendente(asc);
                  }}
                  className={styles.headerGoles}
                  title="Goles en contra"
                />
              </tr>
            </thead>
            <tbody>
              {FuncionesOrdenamiento.ordenarRivales(rivales, ordenPor, ascendente).map((rival) => (
                <tr key={rival.paisRival?.id || rival.id}>
                  <td className={styles.colRival}>{rival.paisRival?.nombre || 'N/A'}</td>
                  <td className={styles.colTotal}>{rival.totalPartidos || 0}</td>
                  <td className={styles.colPartidos}>{rival.ganados || 0}</td>
                  <td className={styles.colPartidos}>{rival.empatados || 0}</td>
                  <td className={styles.colPartidos}>{rival.perdidos || 0}</td>
                  <td className={styles.colGoles}>{rival.golesAFavor || 0}</td>
                  <td className={styles.colGoles}>{rival.golesEnContra || 0}</td>
                </tr>
              ))}
              <tr className={styles.filaTotal}>
                <td className={styles.colRival}>
                  <strong>TOTALES</strong>
                </td>
                <td className={styles.colTotal}>
                  <strong>{totales.totalPartidos}</strong>
                </td>
                <td className={styles.colPartidos}>
                  <strong>{totales.ganados}</strong>
                </td>
                <td className={styles.colPartidos}>
                  <strong>{totales.empatados}</strong>
                </td>
                <td className={styles.colPartidos}>
                  <strong>{totales.perdidos}</strong>
                </td>
                <td className={styles.colGoles}>
                  <strong>{totales.golesAFavor}</strong>
                </td>
                <td className={styles.colGoles}>
                  <strong>{totales.golesEnContra}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {!isCargando && (!rivales || rivales.length === 0) && !erroresGet && (
        <p className={styles.mensajeVacio}>No hay enfrentamientos registrados</p>
      )}
    </div>
  );
};

export default BaseEnfrentamientos;
